/**
 * VRHandHandler - Handles VR hand tracking
 *
 * Pure hand tracking logic with no branching - knows exactly how to handle hands.
 */

import * as THREE from 'three';
import { OculusHandModel } from 'three/addons/webxr/OculusHandModel.js';
import { OculusHandPointerModel } from 'three/addons/webxr/OculusHandPointerModel.js';

import { Actor } from '../actors/Actor.js';
import { CollisionShapeComponent } from '../components/CollisionShapeComponent.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { DefaultCollisionProfile } from '../systems/collision/CollisionTypes.js';

import { VRDeviceHandler } from './VRDeviceHandler.js';

import type { World } from '../game/World.js';

type XRHandSpaceLike = THREE.Group & { joints?: Record<string | number, THREE.Object3D> };

export class VRHandHandler extends VRDeviceHandler {
  private handGroup: XRHandSpaceLike | null = null;
  private handModel: OculusHandModel | null = null;
  private handPointer: OculusHandPointerModel | null = null;
  private pointerActor: Actor | null = null;
  private pointerCollider: CollisionShapeComponent | null = null;

  private getXRHandTipObject(): THREE.Object3D | null {
    if (!this.handGroup) return null;
    const hand = this.handGroup;
    if (hand.joints) {
      const tip = hand.joints['index-finger-tip'];
      return tip ?? null;
    }
    return null;
  }

  constructor(
    inputSource: XRInputSource,
    renderer: THREE.WebGLRenderer,
    world: World,
    controllerIndex: number
  ) {
    super(inputSource, renderer, world, controllerIndex);
    this.createVisualModel();
  }


  public getTargetRayGroup(): THREE.Group | null {
    if (!this.renderer?.xr.isPresenting) {
      return null;
    }
    return this.handPointer?.pointerObject as THREE.Group | null;
  }

  public getGripGroup(): THREE.Group | null {
    if (!this.renderer?.xr.isPresenting) {
      return null;
    }
    // For hands, return the controller group (target ray space)
    return this.handPointer?.pointerObject as THREE.Group | null;
  }

  public createVisualModel(): void {
    if (!this.renderer) {
      return;
    }

    // Add controller groups to scene (important for ray visualization!)
    const controller = this.renderer.xr.getController(this.controllerIndex);
    this.handGroup = this.renderer.xr.getHand(this.controllerIndex);

    this.world.scene.add(controller);
    this.world.scene.add(this.handGroup);

    // Create hand models
    this.handModel = new OculusHandModel(this.handGroup);
    this.handPointer = new OculusHandPointerModel(this.handGroup, controller);

    this.handGroup.add(this.handModel);
    this.handGroup.add(this.handPointer);

    console.log(`[VRHandHandler] Created hand model for ${this.getHandedness()} hand`);

    // Create fingertip pointer collider (trigger-only)
    this.createPointerCollider();
  }

  private createPointerCollider(): void {
    const world = this.world;
    if (!world || !this.handGroup) return;

    this.pointerCollider = CollisionShapeComponent.create({
      geometry: new THREE.SphereGeometry(1, 16, 12),
      physicsOptions: {
        enabled: true,
        motionType: PhysicsMotionType.KinematicPositionBased,
        gravityScale: 0,
        generateCollisionEvents: true,
        collisionProfile: DefaultCollisionProfile.Trigger
      },
      enableDebugVisualization: false
    });
    this.pointerCollider.name = 'VRPointerCollider';
    this.pointerCollider.scale.setScalar(0.02); // ~2cm radius
    // Make sure physics doesn't try to drive transform
    this.pointerCollider.setPhysicsTransformUpdateFlags({
      sendPosition: true,
      sendRotation: true,
      receivePosition: false,
      receiveRotation: false
    });

    // Create a lightweight actor container for the collider
    this.pointerActor = Actor.create({ rootComponent: this.pointerCollider });
    world.addActor(this.pointerActor);
  }

  public override update(): void {
    if (!this.pointerCollider || !this.handGroup) return;
    // Try to get fingertip from hand joints if available
    let worldPos: THREE.Vector3 | null = null;
    const tip = this.getXRHandTipObject();
    if (tip) {
      tip.updateWorldMatrix(true, false);
      worldPos = new THREE.Vector3().setFromMatrixPosition(tip.matrixWorld);
    }

    if (!worldPos) return;

    // Move kinematic trigger to fingertip position
    this.pointerCollider.setWorldPosition(worldPos);
    const physics = this.pointerCollider.getPhysicsEngine();
    if (physics) {
      physics.updateTransform(this.pointerCollider);
    }
  }

  public dispose(): void {
    if (this.pointerActor) {
      const world = this.world;
      if (world) {
        world.removeActors(this.pointerActor);
      }
      this.pointerActor = null;
      this.pointerCollider = null;
    }
    if (this.handModel && this.handGroup) {
      this.handGroup.remove(this.handModel);
      this.handModel = null;
    }

    if (this.handPointer && this.handGroup) {
      this.handGroup.remove(this.handPointer);
      this.handPointer = null;
    }

    if (this.handGroup && this.handGroup.parent) {
      this.handGroup.parent.remove(this.handGroup);
      this.handGroup = null;
    }

    console.log(`[VRHandHandler] Disposed hand model for ${this.getHandedness()} hand`);
  }
}
